import { Fragment, type ReactNode } from 'react';

import { Label } from '@/components/ui/Label';
import type { Block } from '@/content/serie';
import { cn } from '@/lib/cn';

/** Texte avec `code` en ligne, entre accents graves. */
export function Inline({ text }: { text: string }) {
  return (
    <>
      {text.split(/(`[^`]+`)/).map((part, index) =>
        part.startsWith('`') && part.endsWith('`') ? (
          <code key={index} className="font-sans rounded-xs bg-blanc px-1.5 py-0.5 text-code-inline">
            {part.slice(1, -1)}
          </code>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        ),
      )}
    </>
  );
}

/** Une ligne de commande : commentaire `#` atténué, mot-clé `export` en ciel. */
function CodeLine({ line }: { line: string }) {
  const hash = line.indexOf('#');
  const code = hash === -1 ? line : line.slice(0, hash);
  const comment = hash === -1 ? '' : line.slice(hash);
  const keyword = code.startsWith('export ') ? 'export' : '';

  return (
    <>
      {keyword && <span className="text-ciel">{keyword}</span>}
      {code.slice(keyword.length)}
      {comment && <span className="text-blanc/55">{comment}</span>}
      {'\n'}
    </>
  );
}

function BlockView({ block }: { block: Block }): ReactNode {
  switch (block.type) {
    case 'p':
      return (
        <p className="mb-5">
          <Inline text={block.text} />
        </p>
      );

    case 'code':
      return (
        <pre className="font-sans mt-2 mb-7 overflow-x-auto rounded-md bg-encre px-5.5 py-5 text-code text-blanc/90 tabular-nums">
          {block.code.split('\n').map((line, index) => (
            <CodeLine key={index} line={line} />
          ))}
        </pre>
      );

    case 'video':
      // Emplacement du lecteur intégré, à brancher (maquette).
      return (
        <>
          <div
            role="img"
            aria-label="Vidéo de démonstration"
            className="mt-2 mb-2.5 flex aspect-video flex-col items-center justify-center gap-3.5 rounded-md bg-blanc"
          >
            <span className="flex size-16.5 items-center justify-center rounded-full bg-encre">
              <span className="ml-[5px] border-y-[11px] border-l-[18px] border-y-transparent border-l-ciel" />
            </span>
            <Label>{block.label}</Label>
          </div>
          <p className="mb-7 text-body-2xs leading-normal text-encre-mute tabular-nums">{block.caption}</p>
        </>
      );

    case 'table':
      return (
        <div className="mt-2 mb-7 overflow-x-auto">
          <table className="w-full border-collapse text-body-s">
            <thead>
              <tr>
                {block.head.map((cell) => (
                  <th
                    key={cell}
                    className="label border-b border-encre pr-3 pb-2.5 text-left text-tag font-normal text-encre-mute"
                  >
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row) => (
                <tr key={row.cells[0]}>
                  {row.cells.map((cell, index) => (
                    <td
                      key={index}
                      className={cn(
                        'border-b border-line py-3 pr-3',
                        index > 0 && 'text-body-xs whitespace-nowrap tabular-nums',
                        index > 0 && row.highlight && 'font-medium shadow-[inset_0_-3px_0_var(--color-ciel)]',
                      )}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'panne':
      return (
        <div className="mt-2 mb-7 rounded-md bg-blanc px-5.5 py-5">
          <Label tone="encre">{block.label}</Label>
          <p className="mt-2 text-body">{block.text}</p>
        </div>
      );
  }
}

export function Blocks({ blocks }: { blocks: Block[] }) {
  return (
    <>
      {blocks.map((block, index) => (
        <BlockView key={index} block={block} />
      ))}
    </>
  );
}
